import axios from "axios";
import {Contact, contactApi, ContactSearchRow } from "@/api/contacts";


export enum LanguageType {
  DE = "DE",
  EN = "EN",
  FR = "FR",
}

export interface Flame {
  id: string | null;
  title: string;
  titleWithContact?: string | null;
  description: string;
  currentStep: string;
  contactRef: string | null;
  assigneeRef: string | null;
  blackBoardRef: string;
  created: Date;
  updated: Date | null;
  reminderAtUtc: Date | null;
  countComments?: number;
  dossier: Dossier | null;
  basedOnTaskRef: string | null;
  emailRef: string | null;
  deletedUtc: Date | null;
  archivedUtc: Date | null;
  dossierName?: string | null;
  paid: boolean;
  packed: boolean;
  important: boolean;
  proxyOwner?: string | null;
  proxyOwnerFrom?: Date | null;
  proxyOwnerUntil?: Date | null;
  priceFrameIsenschmid?: number | null;
  customerPriceFrameGross?: number | null;
  customerPriceFrameNet?: number | null;
  weightFrameInKg?: number | null;
  frameCalculationParams?: string | null;
  tubeCalculationParams?: string | null;
  priceTubeNet?: number | null;
  priceTubeCustomerGross?: number | null;
  weightTubeInKg?: number | null;
  version: number;
  tags: string[] | [];
}

export interface FlameSearchRequest {
  searchTerm?: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDesc: boolean;
  assigneeRef?: string
  archived?: boolean;
  reminderFlames?: boolean;
}

export interface FlameSearchResult {
  totalPages: number;
  totalElements: number;
  page: number;
  pageSize: number;
  tasks: Flame[];
}

export interface Dossier {
  id: string | null;
  certificateName: string | null;
  language: LanguageType | null;
  deliveryAddressRef: string | null;
  billingAddressRef: string | null;
  pickupDate: Date | null;
  selfPickupAt: Date | null;
  deliveryDate: Date | null;
  currency: string | null;
  paymentConditionRef: string | null;
  deliveryConditionRef: string | null;
  articles: Article[];
  packageInfos?: PackageInfo[];
  noteForArticles: string | null;
  taxFreeDelivery: boolean;
  resellerRef: string | null;
  notes: string | null;
  avisPhoneNumberRef: string | null;
  avisEmailRef: string | null;
  avisPacking: string | null;
  avisText: string | null;
  sapOrdernumber: string | null;
  deliveryIsenschmid: Date | null;
  shipmentReference: string | null;
  paymentPeriod: Date | null;
  oss: boolean;
  forwardingAgencyRef: string | null;
  version: number
}

export interface Article {
  id: string | null;
  productName: string;
  quantity: number;
  discount: number | null;
  orderKey: number;
  warehouse: string | null;
  description: string | null;
  version: number
}

export interface PackageInfo {
  id: string | null;
  description: string | null;
  quantity: number;
  orderKey: number;
  widthInCm: number;
  heightInCm: number;
  depthInCm: number;
  weightInKg: number;
  version: number
}

export async function formatTitle(task: Flame): Promise<string> {
  if( (!task.title || task.title.length == 0 ) && task.contactRef) {
    const contact = await contactApi.getById(task.contactRef)
    return contact.fullName ?? ""
  }
  return task.title;
}

export interface RevertArchivedFlameRequest {
  flameId: string;
  taskboardRef: string;
  step: string;
  assigneeRef: string;
}

interface FlameApi {
  listByTaskBoard(
    taskboardId: string | null,
    contact: Contact | null,
    withFutureTask: boolean
  ): Promise<Flame[]>;
  listAllOpenTasks(searchRequest: FlameSearchRequest): Promise<FlameSearchResult>;
  listAllArchivedFlames(searchRequest: FlameSearchRequest): Promise<FlameSearchResult>;
  listOverDueFlames(
    searchRequest: FlameSearchRequest
  ): Promise<FlameSearchResult>;
  listTasksForPickupCalendar(start: string, end: string): Promise<Flame[]>;
  add(task: Flame): Promise<string>;
  edit(id: string | null, task: Flame): Promise<Flame>;
  move(
    taskId: string,
    taskboardId: string,
    step: string,
    assigneeRef: string | null
  ): Promise<void>;
  delete(id: string): Promise<void>;
  deletePermanent(id: string): Promise<void>;
  revertDeletedTask(id: string): Promise<void>;
  archive(
    id: string,
    refreshProductForContactId: string | null,
    toCustomer: boolean
  ): Promise<void>;
  duplicate(id: string, taskboardId: string, step: string): Promise<void>;
  findDeletedTaskForTaskboard(taskboardId: string): Promise<Flame[]>;
  getById(id: string): Promise<Flame | null>;
  countTasksByContact(contact: Contact): Promise<number>;
  listByContact(contact: Contact | ContactSearchRow): Promise<Flame[]>;
  mapDatesForTask(task: Flame): Flame;
  prioritize(flame: Flame): Promise<void>;
  revertArchivedFlame(RevertArchivedFlameRequest: RevertArchivedFlameRequest): Promise<void>
}

export const flameApi: FlameApi = {
  async listByTaskBoard(taskboardId, contact, withFutureTask) {
    const uri = contact?.id
      ? `/api/flames/blackboard/${taskboardId}?contactRef=${contact.id}&withFutureTask=${withFutureTask}`
      : `/api/flames/blackboard/${taskboardId}?withFutureTask=${withFutureTask}`;
    const result = (await axios.get(uri)).data;

    if (result) {
      return result.map( (v: Flame) => this.mapDatesForTask(v));
    }
    return [];
  },
  async listTasksForPickupCalendar(start, end) {
    const result = (
      await axios.get(`/api/flames/pickup/_list?start=${start}&end=${end}`)
    ).data;

    if (result) {
      return result.map((v: Flame) => this.mapDatesForTask(v));
    }
    return [];
  },
  async listAllOpenTasks(searchRequest) {
    return (await axios.post(`/api/flames/_search`, searchRequest)).data;
  },
  async listAllArchivedFlames(searchRequest) {
    searchRequest.archived = true;
    return (await axios.post(`/api/flames/_search`, searchRequest)).data;
  },
  async listOverDueFlames(searchRequest) {
    searchRequest.reminderFlames = true;
    const result =  (await axios.post(`/api/flames/_search`, searchRequest))
      .data;
    if (result) {
      result.tasks.forEach((task: any) => {
          task.reminderAtUtc =task.reminderAtUtc ? new Date(task.reminderAtUtc) : null
          task.reminderAtUtc?.setSeconds(0);
          task.reminderAtUtc?.setMilliseconds(0);
        })
      return result
    }
    return [];
  },
  async edit(id, flame) {
    const result = (await axios.put(`/api/flames/${id}`, flame)).data;
    return this.mapDatesForTask(result);
  },
  async prioritize(flame) {
    return await axios.put(`/api/flames/${flame.id}/prioritize`);
  },
  async move(id, taskboardId, step, assigneeRef) {
    return await axios.put(`/api/flames/${id}/_move`, {
      taskboardRef: taskboardId,
      step: step,
      assigneeRef: assigneeRef,
    });
  },

  async duplicate(id, taskboardId, step) {
    return await axios.post(`/api/flames/${id}/_duplicate`, {
      taskboardRef: taskboardId,
      step: step,
    });
  },

  async add(task) {
    return (await axios.post("/api/flames", task)).data;
  },

  async delete(id) {
    return await axios.delete(`/api/flames/${id}`);
  },

  async deletePermanent(id: string) {
    return await axios.delete(`/api/flames/${id}/_permanent`);
  },

  async revertDeletedTask(id: string) {
    return await axios.put(`/api/flames/${id}/_revertDeletedTask`);
  },

  async revertArchivedFlame(revertArchivedFlameRequest) {
    return await axios.put(`/api/flames/${revertArchivedFlameRequest.flameId}/revertArchivedFlame`, revertArchivedFlameRequest);
  },


  async archive(id, refreshProductForContactId, toCustomer) {
    if (!refreshProductForContactId) {
      return await axios.delete(
        `/api/flames/${id}/_archive?toCustomer=${toCustomer}`
      );
    } else {
      return await axios.delete(
        `/api/flames/${id}/_archive?toCustomer=${toCustomer}&refreshProductForContactId=${refreshProductForContactId}`
      );
    }
  },
  async findDeletedTaskForTaskboard(taskboardId) {
    const result = (
      await axios.get(`/api/flames/blackboard/${taskboardId}/deleted`)
    ).data;

    if (result) {
      return result.map((v: Flame) => this.mapDatesForTask(v));
    }
    return [];
  },

  async getById(taskId) {
    const result = (await axios.get(`/api/flames/${taskId}`)).data;

    if (result) {
      return this.mapDatesForTask(result);
    }
    return null;
  },
  async countTasksByContact(contact) {
    return (
      await axios.get(`/api/flames/_countByContact?contactRef=${contact.id}`)
    ).data;
  },
  async listByContact(contact) {
    const result = (await axios.get(`/api/contacts/${contact.id}/tasks`)).data;

    if (result) {
      return result.map(
        (v: Flame) => this.mapDatesForTask(v));
    }
    return [];
  },

  mapDatesForTask(flame: Flame): Flame {
    const mappedFlame =  {
    ...flame,
        created: flame.created ? new Date(flame.created) : null,
        updated: flame.updated ? new Date(flame.updated) : null,
        proxyOwnerFrom: flame.proxyOwnerFrom ? new Date(flame.proxyOwnerFrom) : null,
        proxyOwnerUntil: flame.proxyOwnerUntil ? new Date(flame.proxyOwnerUntil) : null,
        reminderAtUtc: flame.reminderAtUtc ? new Date(flame.reminderAtUtc) : null,
        dossier: {
    ...flame.dossier,
          deliveryDate: flame.dossier?.deliveryDate
          ? new Date(flame.dossier.deliveryDate)
          : null,
          selfPickupAt: flame.dossier?.selfPickupAt
          ? new Date(flame.dossier.selfPickupAt)
          : null,
          pickupDate: flame.dossier?.pickupDate
          ? new Date(flame.dossier.pickupDate)
          : null,
          deliveryIsenschmid: flame.dossier?.deliveryIsenschmid
          ? new Date(flame.dossier.deliveryIsenschmid)
          : null,
          paymentPeriod: flame.dossier?.paymentPeriod
          ? new Date(flame.dossier.paymentPeriod)
          : null,
    },
    } as Flame
    mappedFlame.reminderAtUtc?.setSeconds(0);
    mappedFlame.reminderAtUtc?.setMilliseconds(0);
    mappedFlame.proxyOwnerFrom?.setSeconds(0);
    mappedFlame.proxyOwnerFrom?.setMilliseconds(0);
    mappedFlame.proxyOwnerUntil?.setSeconds(0);
    mappedFlame.proxyOwnerUntil?.setMilliseconds(0);
    mappedFlame.dossier?.deliveryDate?.setSeconds(0);
    mappedFlame.dossier?.deliveryDate?.setMilliseconds(0);
    mappedFlame.dossier?.selfPickupAt?.setSeconds(0);
    mappedFlame.dossier?.selfPickupAt?.setMilliseconds(0);
    mappedFlame.dossier?.pickupDate?.setSeconds(0);
    mappedFlame.dossier?.pickupDate?.setMilliseconds(0);
    mappedFlame.dossier?.deliveryIsenschmid?.setSeconds(0);
    mappedFlame.dossier?.deliveryIsenschmid?.setMilliseconds(0);
    mappedFlame.dossier?.paymentPeriod?.setSeconds(0);
    mappedFlame.dossier?.paymentPeriod?.setMilliseconds(0);

    return mappedFlame;
  }
};
